// Discover.jsx - Static Web Version without ThemeContext
import React, { useState } from 'react';
import { FiCalendar, FiUsers, FiSearch, FiHome, FiCompass, FiMinus, FiPlus, FiArrowRight } from 'react-icons/fi';
import { MdFlight, MdLocationOn, MdSwapVert, MdClose } from 'react-icons/md';
import { FaPlane, FaSync } from 'react-icons/fa';

const Discover = () => {
  // Static state for UI demonstration
  const [departureAirport, setDepartureAirport] = useState({ 
    code: 'JFK', 
    name: 'John F. Kennedy International', 
    city: 'New York' 
  });
  
  const [arrivalAirport, setArrivalAirport] = useState({ 
    code: 'CAI', 
    name: 'Cairo International Airport', 
    city: 'Cairo' 
  });
  
  const [departureDate, setDepartureDate] = useState('2024-03-15');
  const [returnDate, setReturnDate] = useState('2024-03-22');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [isRoundTrip, setIsRoundTrip] = useState(true);
  const [passengerModalOpen, setPassengerModalOpen] = useState(false);

  // Hardcoded colors
  const colors = {
    mainPink: '#ff4d6d',
    backgroundColor: '#f8f9fa',
    textColor: '#2d3436',
    textGray: '#6c757d',
    borderColor: '#e9ecef',
    cardBackground: '#ffffff'
  };

  const totalPassengers = adults + children + infants;

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const swapAirports = () => {
    const temp = departureAirport;
    setDepartureAirport(arrivalAirport);
    setArrivalAirport(temp);
  };

  // Popular destinations data
  const popularDestinations = [
    { city: 'Giza', country: 'Egypt', image: 'https://media.voguearabia.com/photos/6808cf6ef6536e69206b6bc8/3:2/w_2240,c_limit/1737979506_informations22.jpg' },
  {city: 'Sharm El Sheikh', 
    country: 'Egypt', 
    image: 'https://www.cameldive.com/wp-content/uploads/190729_Tiran_DSC03188.jpg'
  },
  { 
    city: 'Hurghada', 
    country: 'Egypt', 
    image: 'https://www.egyptsunmarine.com/storage/hurghada-islands.jpg',
  },
  {
    city: 'Luxor', 
    country: 'Egypt', 
    image: 'https://cityandseaadonis.com/wp-content/uploads/2025/10/luxor-temple-main-view-beautiful-sunset-light-egypt-scaled.jpg'
  },
  { 
    city: 'Siwa', 
    country: 'Egypt', 
    image: 'https://www.egypttoursportal.com/images/2025/07/Geology-of-Siwa-Oasis-Egypt-Tours-Portal.jpg' 
  },
  { 
    city: 'Aswan', 
    country: 'Egypt', 
    image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTgCBngLGe_scwAY1tsPYH8nroAJv2OYMQdTA&s' 
  }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Background Image */}
      <div className="h-[300px] w-full relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1200)' }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40" />
        </div>
        
        <div className="relative h-full flex flex-col justify-end px-8 pb-12">
          <h1 className="text-5xl font-bold text-white mb-2 drop-shadow-lg">
            Plan Your Adventure
          </h1>
          <p className="text-lg text-white opacity-90  drop-shadow ">
            {arrivalAirport?.city ? `${arrivalAirport.city}, ${arrivalAirport.country || 'Destination'}` : 'Discover amazing destinations'}
          </p>

          {/* Chat Button */}
          <button className="absolute top-6 right-6 flex items-center bg-white bg-opacity-15 px-4 py-2 rounded-full border border-white border-opacity-30 hover:bg-opacity-25 transition-all">
            <span className="text-white font-semibold mr-2 text-lg drop-shadow">
              Chat with Fern
            </span>
            <div className="w-10 h-10 bg-gradient-to-br from-[#e54959] to-[#e85e6c] rounded-full flex items-center justify-center text-white font-bold text-xl">
              AI
            </div>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4  sm:px-6 lg:px-8 -mt-8 relative z-10 pb-16">
        
        {/* Stats Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 flex">
          <div className="flex-1 text-center">
            <FiCalendar className="mx-auto text-2xl" style={{ color: colors.mainPink }} />
            <div className="font-bold text-lg mt-2">{formatDateForDisplay(departureDate)}</div>
            <div className="text-xs text-gray-500">Departure</div>
          </div>
          
          <div className="w-px h-12 bg-gray-200 self-center" />
          
          <div className="flex-1 text-center">
            <FiUsers className="mx-auto text-2xl" style={{ color: colors.mainPink }} />
            <div className="font-bold text-lg mt-2">{totalPassengers}</div>
            <div className="text-xs text-gray-500">Travelers</div>
          </div>
          
          <div className="w-px h-12 bg-gray-200 self-center" />
          
          <div className="flex-1 text-center">
            <FaPlane className="mx-auto text-2xl" style={{ color: colors.mainPink }} />
            <div className="font-bold text-lg mt-2">{isRoundTrip ? 'Round' : 'One Way'}</div>
            <div className="text-xs text-gray-500">Trip Type</div>
          </div>
        </div>

        {/* Search Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          
          {/* Trip Type Toggle */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => setIsRoundTrip(true)}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-xl border transition-all ${
                isRoundTrip 
                  ? 'bg-[#e54959] text-white border-[#e54959]' 
                  : 'border-pink-500 text-pink-500 hover:bg-pink-50'
              }`}
              style={{ borderColor: isRoundTrip ? '#1f2937' : colors.mainPink }}
            >
              <FaSync className={`mr-2 ${isRoundTrip ? 'text-white' : ''}`} style={{ color: isRoundTrip ? 'white' : colors.mainPink }} />
              <span className="font-semibold">Round Trip</span>
            </button>
            
            <button
              onClick={() => setIsRoundTrip(false)}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-xl border transition-all ${
                !isRoundTrip 
                  ? 'bg-[#e54959] text-white border-[#e54959]' 
                  : 'border-pink-500 text-pink-500 hover:bg-pink-50'
              }`}
              style={{ borderColor: !isRoundTrip ? '#1f2937' : colors.mainPink }}
            >
              <FiArrowRight className={`mr-2 ${!isRoundTrip ? 'text-white' : ''}`} style={{ color: !isRoundTrip ? 'white' : colors.mainPink }} />
              <span className="font-semibold">One Way</span>
            </button>
          </div>

          {/* Airports Selection */}
          <div className="mb-6">
            {/* Departure */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <MdFlight style={{ color: colors.mainPink }} />
                <span className="font-semibold text-gray-700">From</span>
              </div>
              <button className="w-full p-4 border border-gray-200 rounded-xl text-left flex justify-between items-center hover:border-pink-300 transition">
                <div>
                  <span className="font-bold text-gray-800">{departureAirport.code}</span>
                  <span className="text-gray-500 ml-2">{departureAirport.name}</span>
                </div>
                <span className="text-gray-400">▼</span>
              </button>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center -my-2 relative z-10">
              <button
                onClick={swapAirports}
                className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: colors.mainPink }}
              >
                <MdSwapVert className="text-white text-xl" />
              </button>
            </div>

            {/* Arrival */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <MdLocationOn style={{ color: colors.mainPink }} />
                <span className="font-semibold text-gray-700">To</span>
              </div>
              <button className="w-full p-4 border border-gray-200 rounded-xl text-left flex justify-between items-center hover:border-pink-300 transition">
                <div>
                  <span className="font-bold text-gray-800">{arrivalAirport.code}</span>
                  <span className="text-gray-500 ml-2">{arrivalAirport.name}</span>
                </div>
                <span className="text-gray-400">▼</span>
              </button>
            </div>
          </div>

          {/* Dates Selection */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FiCalendar style={{ color: colors.mainPink }} />
                <span className="font-semibold text-gray-700">Departure</span>
              </div>
              <button className="w-full p-4 border border-gray-200 rounded-xl text-left flex justify-between items-center hover:border-pink-300 transition">
                <span className="text-gray-800">{formatDateForDisplay(departureDate)}</span>
                <span className="text-gray-400">▼</span>
              </button>
            </div>

            {isRoundTrip && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FiCalendar style={{ color: colors.mainPink }} />
                  <span className="font-semibold text-gray-700">Return</span>
                </div>
                <button className="w-full p-4 border border-gray-200 rounded-xl text-left flex justify-between items-center hover:border-pink-300 transition">
                  <span className="text-gray-800">{formatDateForDisplay(returnDate)}</span>
                  <span className="text-gray-400">▼</span>
                </button>
              </div>
            )}
          </div>

          {/* Passengers */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <FiUsers style={{ color: colors.mainPink }} />
              <span className="font-semibold text-gray-700">Travelers</span>
            </div>
            <button
              onClick={() => setPassengerModalOpen(true)}
              className="w-full p-4 border border-gray-200 rounded-xl text-left flex justify-between items-center hover:border-pink-300 transition"
            >
              <span className="text-gray-800">{totalPassengers} {totalPassengers === 1 ? 'Traveler' : 'Travelers'}</span>
              <span className="text-gray-400">▼</span>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              className="w-full py-4 rounded-xl text-white font-bold flex items-center justify-center gap-2 shadow-lg hover:opacity-90 transition"
              style={{ backgroundColor: colors.mainPink }}
            >
              <FiSearch className="text-xl" />
              <span>Search Flights</span>
            </button>

            <div className="grid grid-cols-2 gap-4">
              <button
                className="py-3 rounded-xl border flex items-center justify-center gap-2 hover:bg-pink-50 transition"
                style={{ borderColor: colors.mainPink, color: colors.mainPink }}
              >
                <FiHome />
                <span>Hotels</span>
              </button>

              <button
                className="py-3 rounded-xl border flex items-center justify-center gap-2 hover:bg-pink-50 transition"
                style={{ borderColor: colors.mainPink, color: colors.mainPink }}
              >
                <FiCompass />
                <span>Activities</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition border border-gray-100 text-left">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.mainPink}20` }}>
                <FiCompass className="text-2xl" style={{ color: colors.mainPink }} />
              </div>
              <h3 className="font-bold text-gray-800 mb-1">Explore</h3>
              <p className="text-sm text-gray-500">Find nearby spots</p>
            </button>

            <button className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition border border-gray-100 text-left">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.mainPink}20` }}>
                <FiHome className="text-2xl" style={{ color: colors.mainPink }} />
              </div>
              <h3 className="font-bold text-gray-800 mb-1">Budget Planner</h3>
              <p className="text-sm text-gray-500">Plan expenses</p>
            </button>

            <button className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition border border-gray-100 text-left">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.mainPink}20` }}>
                <FiUsers className="text-2xl" style={{ color: colors.mainPink }} />
              </div>
              <h3 className="font-bold text-gray-800 mb-1">Interests</h3>
              <p className="text-sm text-gray-500">Personalize preferences</p>
            </button>
          </div>
        </div>

        {/* Popular Destinations */}
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Popular Destinations</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularDestinations.map((dest, index) => (
              <button
                key={index}
                className="relative h-48 rounded-xl overflow-hidden group"
                onClick={() => setArrivalAirport({
                  code: dest.city.substring(0, 3).toUpperCase(),
                  name: `${dest.city} Airport`,
                  city: dest.city,
                  country: dest.country
                })}
              >
                <img 
                  src={dest.image} 
                  alt={dest.city}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-40 transition" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-bold drop-shadow-lg">{dest.city}</h3>
                  <p className="text-sm opacity-90 drop-shadow">{dest.country}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Passenger Modal */}
      {passengerModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-t-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">Select Travelers</h3>
              <button onClick={() => setPassengerModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <MdClose className="text-xl" />
              </button>
            </div>

            {/* Adults */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <p className="font-semibold text-gray-800">Adults</p>
                <p className="text-sm text-gray-500">Age 12+</p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setAdults(Math.max(1, adults - 1))}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                >
                  <FiMinus />
                </button>
                <span className="font-bold text-lg w-8 text-center">{adults}</span>
                <button
                  onClick={() => setAdults(Math.min(9, adults + 1))}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:opacity-90"
                  style={{ backgroundColor: colors.mainPink }}
                >
                  <FiPlus />
                </button>
              </div>
            </div>

            {/* Children */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <p className="font-semibold text-gray-800">Children</p>
                <p className="text-sm text-gray-500">Age 2-11</p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setChildren(Math.max(0, children - 1))}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                >
                  <FiMinus />
                </button>
                <span className="font-bold text-lg w-8 text-center">{children}</span>
                <button
                  onClick={() => setChildren(Math.min(9, children + 1))}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:opacity-90"
                  style={{ backgroundColor: colors.mainPink }}
                >
                  <FiPlus />
                </button>
              </div>
            </div>

            {/* Infants */}
            <div className="flex justify-between items-center mb-8">
              <div>
                <p className="font-semibold text-gray-800">Infants</p>
                <p className="text-sm text-gray-500">Under 2</p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setInfants(Math.max(0, infants - 1))}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                >
                  <FiMinus />
                </button>
                <span className="font-bold text-lg w-8 text-center">{infants}</span>
                <button
                  onClick={() => setInfants(Math.min(9, infants + 1))}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:opacity-90"
                  style={{ backgroundColor: colors.mainPink }}
                >
                  <FiPlus />
                </button>
              </div>
            </div>

            <button
              onClick={() => setPassengerModalOpen(false)}
              className="w-full py-4 rounded-xl text-white font-bold"
              style={{ backgroundColor: colors.mainPink }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discover;